'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { tw } from '@tw';
import type {
  SubscriptionChangePreviewDto,
  SubscriptionDto,
  SubscriptionPlanDto,
} from '@peach/types/subscriptions/subscription.dto.js';
import { PAUSE_WEEKS_OPTIONS, planMove } from '@peach/types/subscriptions/types.js';
import { ScheduleLimits } from '@peach/types/tutors/consts.js';
import { cancelClass } from '@/app/actions/classes';
import {
  bookSubscriptionCall,
  cancelSubscription,
  changeSubscriptionPlan,
  previewSubscriptionChange,
  settleSubscriptionChange,
  pauseSubscription,
} from '@/app/actions/subscriptions';
import { callAction } from '@/lib/call-action';
import { useTimezone } from '@/lib/hooks/useTimezone';
import { useClock } from '@/lib/hooks/useClock';
import { useCalendarStore } from '@/lib/stores/calendar.store';
import { formatDate, formatDateInTz } from '@/lib/utils/date';
import { formatPrice } from '@/lib/utils/format';
import { stripeFor } from '@/lib/stripe-client';
import { SlotPicker } from '../SlotPicker';
import { planName, planTotal } from './plan-copy';

interface SubscriptionCardProps {
  subscription: SubscriptionDto;
  /** The creator's live plans, to move to. */
  plans: SubscriptionPlanDto[];
  tutorUsername: string;
  tutorName: string;
  serverNow: number;
  /** Opens the tip; the card asks for one on the call that just ended. */
  onTip?: () => void;
}

/** A subscriber's whole subscription in one card: their calls, the calendar, and the plan. */
export function SubscriptionCard({
  subscription: sub,
  plans,
  tutorUsername,
  tutorName,
  serverNow,
  onTip,
}: SubscriptionCardProps) {
  const router = useRouter();
  const timezone = useTimezone();
  const now = useClock(serverNow);
  const calendarChanged = useCalendarStore((s) => s.changed);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const [openWeek, setOpenWeek] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<{
    planId: string;
    preview: SubscriptionChangePreviewDto | null;
  } | null>(null);

  const membership = sub.plan.callsPerWeek === 0;
  const pausedNow =
    !!sub.pausedFrom &&
    !!sub.pausedUntil &&
    new Date(sub.pausedFrom).getTime() <= now &&
    new Date(sub.pausedUntil).getTime() > now;
  const pauseAhead =
    !!sub.pausedFrom && !!sub.pausedUntil && new Date(sub.pausedUntil).getTime() > now;
  // The server's gates: no plan change while ending, paused or unpaid.
  const changeable = !sub.cancelAt && !pauseAhead && !sub.paymentDue;
  // Moving up also waits while a smaller plan is chosen: Stripe already bills that one.
  const bigger =
    changeable && !sub.nextPlan ? plans.filter((plan) => planMove(sub.plan, plan) === 'up') : [];
  const smaller = changeable
    ? plans.filter((plan) => planMove(sub.plan, plan) === 'down' && plan.id !== sub.nextPlan?.id)
    : [];
  const renewsOn = formatDate(sub.paidThrough, 'MMM D');

  const act = useCallback(
    async (run: () => Promise<{ success: boolean; error?: string }>) => {
      setBusy(true);
      setError(null);
      const result = await callAction(run);
      setBusy(false);
      if (!result.success) setError(result.error ?? 'Something went wrong, try again');
      else {
        setUpgrade(null);
        router.refresh();
      }
    },
    [router]
  );

  const book = useCallback(
    async (isoTime: string, tz: string) => {
      const result = await callAction(() =>
        bookSubscriptionCall(sub.id, isoTime, tz, tutorUsername)
      );
      if (!result.success) return result.error;
      calendarChanged(tutorUsername);
      setOpenWeek(null);
      router.refresh();
    },
    [calendarChanged, router, sub.id, tutorUsername]
  );

  const money = (amount: number) => formatPrice({ amount, currency: sub.plan.currency });

  // The charge is read from Stripe before the tap that makes it, never worked out here.
  const previewUpgrade = useCallback(
    async (planId: string) => {
      setError(null);
      setUpgrade({ planId, preview: null });
      const result = await callAction(() => previewSubscriptionChange(sub.id, planId));
      if (!result.success) {
        setUpgrade(null);
        setError(result.error ?? 'Something went wrong, try again');
      } else setUpgrade({ planId, preview: result.data });
    },
    [sub.id]
  );

  // A bank that asks to confirm the upgrade gets its check here, in Stripe's own dialog.
  const upgradeTo = useCallback(
    async (planId: string, prorationDate: number) => {
      setBusy(true);
      setError(null);
      const result = await callAction(() =>
        changeSubscriptionPlan(sub.id, planId, tutorUsername, prorationDate)
      );
      let failure = result.success ? null : (result.error ?? 'Something went wrong, try again');
      if (result.success && result.data.clientSecret && result.data.publishableKey) {
        const stripe = await stripeFor(result.data.publishableKey);
        const confirmed = await stripe?.confirmCardPayment(result.data.clientSecret);
        await callAction(() => settleSubscriptionChange(sub.id, tutorUsername));
        if (!confirmed || confirmed.error) {
          failure =
            confirmed?.error?.message ??
            'Your bank did not confirm the payment, nothing was charged';
        }
      }
      setBusy(false);
      if (failure) {
        // The confirm then shows today's amount, never the one that was refused.
        await previewUpgrade(planId);
        setError(failure);
      } else {
        setUpgrade(null);
        router.refresh();
      }
    },
    [previewUpgrade, router, sub.id, tutorUsername]
  );

  const cancelCall = useCallback(
    async (classId: string) => {
      const result = await callAction(() => cancelClass(classId));
      if (!result.success) return result.error ?? 'Something went wrong, try again';
      calendarChanged(tutorUsername);
      router.refresh();
    },
    [calendarChanged, router, tutorUsername]
  );
  // The server's rule too: no cancel inside the notice window.
  const cancellable = (start: string) =>
    Date.parse(start) - now > ScheduleLimits.minHoursBeforeClass * 60 * 60 * 1000;

  const status = sub.cancelAt
    ? `Ends ${formatDate(sub.cancelAt, 'MMM D')}`
    : sub.paymentDue
      ? 'Payment due'
      : pausedNow
        ? `Paused until ${formatDate(sub.pausedUntil!, 'MMM D')}`
        : sub.nextPlan
          ? `Moves to ${planName(sub.nextPlan.callsPerWeek).toLowerCase()} on ${renewsOn} for ${planTotal(sub.nextPlan)}`
          : `Renews ${renewsOn} for ${planTotal(sub.plan)}`;
  const when = (iso: string) =>
    timezone ? formatDateInTz(iso, 'ddd, MMM D [at] HH:mm', timezone) : '…';

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-head-3 font-extrabold text-[var(--sf-ink)]">
          {planName(sub.plan.callsPerWeek)}
        </span>
        <span className="sf-note shrink-0">{status}</span>
      </div>
      {sub.payUrl && (
        <>
          <span className="sf-note">
            Your last payment did not go through. Your calls open again once it does.
          </span>
          <a href={sub.payUrl} className="mm-btn sf-cta sf-cta-ink">
            Pay now
          </a>
        </>
      )}
      {sub.lastCall && onTip && (
        <div className="flex items-center justify-between gap-3 rounded-[10px] bg-[var(--sf-card)] px-3 py-2">
          <span className="text-p-3 text-[var(--sf-ink)]" suppressHydrationWarning>
            Say thanks for your call on{' '}
            {timezone ? formatDateInTz(sub.lastCall, 'ddd, MMM D', timezone) : '…'}
          </span>
          <button
            type="button"
            onClick={onTip}
            aria-label={`Tip ${tutorName} for your call`}
            className="mm-btn sf-cta sf-cta-line !w-auto shrink-0 px-3"
          >
            Tip
          </button>
        </div>
      )}
      {membership ? (
        <>
          {sub.calls.map((call) => (
            <CallRow
              key={call.id}
              id={call.id}
              when={when(call.start)}
              onCancel={cancellable(call.start) ? () => cancelCall(call.id) : undefined}
            />
          ))}
        </>
      ) : sub.weeks.length === 0 && !sub.payUrl ? (
        <span className="sf-note">Your calls open again once your next payment goes through</span>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {sub.weeks.map((week, i) => {
            const calls = sub.calls.filter(
              (call) => call.start >= week.start && call.start < week.end
            );
            const open = openWeek === week.start;
            return (
              <li
                key={week.start}
                className="flex flex-col gap-2 rounded-[10px] bg-[var(--sf-page)] px-3 py-2.5"
              >
                <span className="text-p-3 font-bold text-[var(--sf-ink)]">
                  {i === 0 ? 'This week' : 'Next week'}
                </span>
                {calls.map((call) => (
                  <CallRow
                    key={call.id}
                    id={call.id}
                    when={when(call.start)}
                    onCancel={cancellable(call.start) ? () => cancelCall(call.id) : undefined}
                  />
                ))}
                {week.paused ? (
                  <span className="sf-note">Paused</span>
                ) : week.callsLeft === 0 ? (
                  calls.length === 0 && <span className="sf-note">Nothing left to book</span>
                ) : open ? (
                  <>
                    <SlotPicker
                      tutorUsername={tutorUsername}
                      calendar={{ courseId: sub.courseId }}
                      within={{ from: week.start, to: week.end }}
                      empty={`No times open that week, ${tutorName} may open more`}
                      serverNow={serverNow}
                      busyLabel="Booking…"
                      label={(time) =>
                        time
                          ? `Book ${time.format('ddd, MMM D')} at ${time.format('HH:mm')}`
                          : 'Pick a day and time'
                      }
                      onPick={book}
                    />
                    <button
                      type="button"
                      onClick={() => setOpenWeek(null)}
                      className="mm-btn sf-cta sf-cta-line"
                    >
                      Not now
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setOpenWeek(week.start)}
                    className="mm-btn sf-cta sf-cta-ink"
                  >
                    {week.callsLeft === 1 ? 'Book your call' : `Book your ${week.callsLeft} calls`}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && <span className="sf-note text-error">{error}</span>}

      <button
        type="button"
        onClick={() => setManaging((m) => !m)}
        className="mm-btn sf-cta sf-cta-line"
        aria-expanded={managing}
      >
        {managing ? 'Done' : 'Manage subscription'}
      </button>

      {managing && (
        <div className="flex flex-col gap-2">
          {bigger.map((plan) =>
            upgrade?.planId === plan.id ? (
              <div key={plan.id} className="flex flex-col gap-2">
                {upgrade.preview ? (
                  <>
                    <span className="sf-note">
                      {`You pay ${money(upgrade.preview.dueToday)} today for the rest of this period on ${planName(plan.callsPerWeek).toLowerCase()}`}
                      {upgrade.preview.credit > 0 &&
                        `, with the ${money(upgrade.preview.credit)} left on your current plan already taken off`}
                      {`. Then ${money(upgrade.preview.renewal)} every 4 weeks.`}
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void upgradeTo(plan.id, upgrade.preview!.prorationDate)}
                      className="mm-btn sf-cta sf-cta-ink"
                    >
                      {busy ? 'Paying…' : `Pay ${money(upgrade.preview.dueToday)}`}
                    </button>
                  </>
                ) : (
                  <span className="sf-note">Working out today&rsquo;s price…</span>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setUpgrade(null)}
                  className="mm-btn sf-cta sf-cta-line"
                >
                  Not now
                </button>
              </div>
            ) : (
              <button
                key={plan.id}
                type="button"
                disabled={busy}
                onClick={() => void previewUpgrade(plan.id)}
                className="mm-btn sf-cta sf-cta-line"
              >
                Move up to {planName(plan.callsPerWeek).toLowerCase()}, {planTotal(plan)} every 4
                weeks
              </button>
            )
          )}

          {smaller.map((plan) => (
            <button
              key={plan.id}
              type="button"
              disabled={busy}
              onClick={() => void act(() => changeSubscriptionPlan(sub.id, plan.id, tutorUsername))}
              className="mm-btn sf-cta sf-cta-line"
            >
              Switch to {planName(plan.callsPerWeek).toLowerCase()} from {renewsOn},{' '}
              {planTotal(plan)} every 4 weeks
            </button>
          ))}
          {changeable && sub.nextPlan && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void act(() => changeSubscriptionPlan(sub.id, sub.plan.id, tutorUsername))
              }
              className="mm-btn sf-cta sf-cta-line"
            >
              Keep {planName(sub.plan.callsPerWeek).toLowerCase()}
            </button>
          )}
          {(smaller.length > 0 || (changeable && sub.nextPlan)) && (
            <span className="sf-note">
              A smaller plan starts at your renewal on {renewsOn}, with nothing to pay now. Until
              then you keep this plan&rsquo;s calls.
            </span>
          )}

          {!membership && !sub.cancelAt && !pauseAhead && !sub.paymentDue && !sub.nextPlan && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="sf-note">Pause from next week for</span>
              {PAUSE_WEEKS_OPTIONS.map((weeks) => (
                <button
                  key={weeks}
                  type="button"
                  disabled={busy}
                  onClick={() => void act(() => pauseSubscription(sub.id, weeks, tutorUsername))}
                  className={tw('mm-btn rounded-full border border-[var(--sf-line)] px-3 py-1')}
                >
                  <span className="sf-num text-p-3 text-[var(--sf-ink)]">
                    {weeks} {weeks === 1 ? 'week' : 'weeks'}
                  </span>
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => void act(() => cancelSubscription(sub.id, !sub.cancelAt, tutorUsername))}
            className="mm-btn sf-cta sf-cta-line"
          >
            {sub.cancelAt ? 'Keep my subscription' : 'Cancel at the end of the paid weeks'}
          </button>
        </div>
      )}
    </div>
  );
}

function CallRow({
  id,
  when,
  onCancel,
}: {
  id: string;
  when: string;
  /** Absent inside the notice window, when the call can no longer be cancelled. */
  onCancel?: () => Promise<string | void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    if (!onCancel) return;
    setBusy(true);
    const failure = await onCancel();
    setBusy(false);
    if (failure) setError(failure);
  };

  return (
    <div className="flex flex-col gap-2 rounded-[10px] bg-[var(--sf-card)] px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        {/* The hour waits for the viewer's zone, as on every call card. */}
        <span className="text-p-3 text-[var(--sf-ink)]" suppressHydrationWarning>
          {when}
        </span>
        {!confirming && (
          <div className="flex shrink-0 gap-1.5">
            {onCancel && (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="mm-btn sf-cta sf-cta-line !w-auto px-3"
              >
                Cancel
              </button>
            )}
            <a href={`/call/${id}`} className="mm-btn sf-cta sf-cta-line !w-auto px-3">
              Join
            </a>
          </div>
        )}
      </div>
      {confirming && (
        <div className="flex flex-col gap-1.5">
          <span className="sf-note">Cancel this call? You can book another time.</span>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => void cancel()}
              className="mm-btn sf-cta sf-cta-ink"
            >
              {busy ? 'Cancelling…' : 'Cancel call'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                setError(null);
              }}
              className="mm-btn sf-cta sf-cta-line"
            >
              Keep it
            </button>
          </div>
          {error && <span className="sf-note text-error">{error}</span>}
        </div>
      )}
    </div>
  );
}
